import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Typography,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Card,
  CardContent,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  Alert,
  Button
} from '@mui/material';
import {
  Add as AddIcon,
  Remove as RemoveIcon,
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  Timeline as TimelineIcon
} from '@mui/icons-material';
import { eventsAPI } from '../../api/events';
import {
  INDICATOR_EVENT_DEFINITIONS,
  getEventTypeLabel,
} from '../../constants/eventTypes';
import DWLFLoader from '../../components/DWLFLoader';
import { describeKeyLevelEvent, isKeyLevelEvent } from '../../utils/eventHelpers';

const EventPicker = ({ selectedEvents = [], onEventsChange, symbol = '' }) => {
  const eventTypeOptions = useMemo(
    () =>
      INDICATOR_EVENT_DEFINITIONS.map((definition) => ({
        value: definition.id,
        label: definition.name,
      })),
    [],
  );
  const preferredDefault =
    eventTypeOptions.find((option) => option.value === 'keyLevel.broken')?.value ||
    eventTypeOptions[0]?.value ||
    'keyLevel.broken';

  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [eventType, setEventType] = useState(preferredDefault);
  const [days, setDays] = useState(30);
  const [symbolFilter, setSymbolFilter] = useState(symbol);

  const fetchEvents = async () => {
    setLoading(true);
    setError('');
    try {
      const response = await eventsAPI.getRecentEvents({ type: eventType, days });
      let filteredEvents = response.events || [];
      
      // Filter by symbol if provided
      if (symbolFilter) {
        filteredEvents = filteredEvents.filter(event => 
          event.symbol.toLowerCase().includes(symbolFilter.toLowerCase())
        );
      }
      
      setEvents(filteredEvents);
    } catch (err) {
      setError('Failed to fetch events');
      console.error('Error fetching events:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchEvents();
  }, [eventType, days, symbolFilter]);

  const handleAddEvent = (event) => {
    const eventKey = `${event.symbol}_${event.eventType}_${event.date}`;
    if (!selectedEvents.find(e => `${e.symbol}_${e.eventType}_${e.date}` === eventKey)) {
      onEventsChange([...selectedEvents, event]);
    }
  };

  const handleRemoveEvent = (eventToRemove) => {
    const eventKey = `${eventToRemove.symbol}_${eventToRemove.eventType}_${eventToRemove.date}`;
    onEventsChange(selectedEvents.filter(event => 
      `${event.symbol}_${event.eventType}_${event.date}` !== eventKey
    ));
  };

  const getEventDescription = (event) => {
    if (!event) {
      return '';
    }
    if (isKeyLevelEvent(event.eventType)) {
      return describeKeyLevelEvent(event);
    }
    switch (event.eventType) {
      case 'trendline_break':
      case 'trendline_break_bullish':
      case 'trendline_break_bearish':
        return `Trendline break at ${event.level || event.breakPrice || 'unknown level'}`;
      case 'trendline_breach':
      case 'trendline_breach_bullish':
      case 'trendline_breach_bearish':
        return `Trendline breach near ${event.level || event.breakPrice || 'unknown level'}`;
      case 'swing_low_formed':
        return `Swing low at ${event.level || 'unknown level'}`;
      case 'swing_high_formed':
        return `Swing high at ${event.level || 'unknown level'}`;
      case 'swing_low_break':
        return `Swing low break at ${event.breakPrice || event.level || 'unknown level'}`;
      case 'swing_high_break':
        return `Swing high break at ${event.breakPrice || event.level || 'unknown level'}`;
      case 'swing_low_sweep':
        return `Swing low sweep at ${event.sweepPrice || event.level || 'unknown level'}`;
      case 'swing_high_sweep':
        return `Swing high sweep at ${event.sweepPrice || event.level || 'unknown level'}`;
      case 'lower_low':
        return `Lower low at ${event.level || 'unknown level'}`;
      case 'lower_high':
        return `Lower high at ${event.level || 'unknown level'}`;
      case 'higher_low':
        return `Higher low at ${event.level || 'unknown level'}`;
      case 'higher_high':
        return `Higher high at ${event.level || 'unknown level'}`;
      case 'cycle.low.higher_low':
        return `Higher cycle low at ${event.pivotPrice || event.level || 'unknown level'}`;
      case 'cycle.low.lower_low':
        return `Lower cycle low at ${event.pivotPrice || event.level || 'unknown level'}`;
      case 'cycle.high.higher_high':
        return `Higher cycle high at ${event.pivotPrice || event.level || 'unknown level'}`;
      case 'cycle.high.lower_high':
        return `Lower cycle high at ${event.pivotPrice || event.level || 'unknown level'}`;
      case 'cycle.low.sweep':
        return `Cycle low sweep at ${event.sweepPrice || event.pivotPrice || event.level || 'unknown level'}`;
      case 'cycle.high.sweep':
        return `Cycle high sweep at ${event.sweepPrice || event.pivotPrice || event.level || 'unknown level'}`;
      case 'ema_bullish_unalignment':
        return 'EMA bullish unalignment';
      case 'ema_bullish_alignment':
        return 'EMA bullish alignment';
      case 'ema_bullish_alignment_sustained':
        return 'EMA bullish alignment sustained';
      case 'ema_bullish_cloud_hit':
        return 'EMA bullish cloud hit';
      case 'ema_bearish_alignment':
        return 'EMA bearish alignment';
      case 'ema_bearish_unalignment':
        return 'EMA bearish unalignment';
      case 'ema_bearish_alignment_sustained':
        return 'EMA bearish alignment sustained';
      case 'ema_bearish_cloud_hit':
        return 'EMA bearish cloud hit';
      default:
        return getEventTypeLabel(event.eventType);
    }
  };

  const getEventIcon = (event) => {
    // Bullish events
    const breakoutDirection = String(event.breakoutDirection || '').toLowerCase();
    if (breakoutDirection === 'up' ||
        breakoutDirection === 'bullish' ||
        event.movementDirection === 'approaching_from_below' ||
        event.eventType === 'higher_high' ||
        event.eventType === 'higher_low' ||
        event.eventType === 'swing_high_formed' ||
        event.eventType === 'swing_high_break' ||
        event.eventType === 'swing_low_sweep' ||
        event.eventType === 'cycle.low.higher_low' ||
        event.eventType === 'cycle.high.higher_high' ||
        event.eventType === 'cycle.low.sweep' ||
        event.eventType === 'ema_bullish_alignment' ||
        event.eventType === 'ema_bullish_alignment_sustained' ||
        event.eventType === 'ema_bullish_cloud_hit' ||
        event.eventType === 'trendline_break_bullish' ||
        event.eventType === 'trendline_breach_bullish') {
      return <TrendingUpIcon color="success" />;
    }
    
    // Bearish events
    if (breakoutDirection === 'down' ||
        breakoutDirection === 'bearish' ||
        event.movementDirection === 'approaching_from_above' ||
        event.eventType === 'lower_low' ||
        event.eventType === 'lower_high' ||
        event.eventType === 'swing_low_formed' ||
        event.eventType === 'swing_low_break' ||
        event.eventType === 'swing_high_sweep' ||
        event.eventType === 'cycle.low.lower_low' ||
        event.eventType === 'cycle.high.lower_high' ||
        event.eventType === 'cycle.high.sweep' ||
        event.eventType === 'ema_bearish_alignment' ||
        event.eventType === 'ema_bearish_alignment_sustained' ||
        event.eventType === 'ema_bearish_cloud_hit' ||
        event.eventType === 'trendline_break_bearish' ||
        event.eventType === 'trendline_breach_bearish') {
      return <TrendingDownIcon color="error" />;
    }
    
    // Neutral events
    if (event.eventType === 'trendline_break' ||
        event.eventType === 'trendline_breach' ||
        event.eventType === 'ema_bullish_unalignment' ||
        event.eventType === 'ema_bearish_unalignment') {
      return <TimelineIcon color="warning" />;
    }
    
    return <TimelineIcon />;
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  return (
    <Box>
      <Typography variant="h6" fontWeight={600} mb={2}>
        Link DWLF Events
      </Typography>

      {/* Filters */}
      <Box
        display="flex"
        gap={2}
        mb={3}
        flexWrap="wrap"
        alignItems="center"
      >
        <TextField
          label="Symbol Filter"
          value={symbolFilter}
          onChange={(e) => setSymbolFilter(e.target.value)}
          placeholder="e.g., TSLA"
          size="small"
          sx={{ flex: '1 1 160px', minWidth: 140 }}
        />

        <FormControl size="small" sx={{ flex: '1 1 180px', minWidth: 160 }}>
          <InputLabel>Event Type</InputLabel>
          <Select
            value={eventType}
            onChange={(e) => setEventType(e.target.value)}
            label="Event Type"
          >
            {eventTypeOptions.map(type => (
              <MenuItem key={type.value} value={type.value}>
                {type.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl size="small" sx={{ flex: '1 1 130px', minWidth: 120 }}>
          <InputLabel>Days</InputLabel>
          <Select
            value={days}
            onChange={(e) => setDays(e.target.value)}
            label="Days"
          >
            <MenuItem value={3}>3 days</MenuItem>
            <MenuItem value={7}>7 days</MenuItem>
            <MenuItem value={14}>14 days</MenuItem>
            <MenuItem value={30}>30 days</MenuItem>
            <MenuItem value={60}>60 days</MenuItem>
            <MenuItem value={90}>90 days</MenuItem>
          </Select>
        </FormControl>
        <Box sx={{ flexShrink: 0 }}>
          <Button
            variant="outlined"
            onClick={fetchEvents}
            disabled={loading}
            size="small"
            sx={{ minWidth: 120 }}
          >
            Refresh
          </Button>
        </Box>
      </Box>

      {/* Selected Events */}
      {selectedEvents.length > 0 && (
        <Box mb={3}>
          <Typography variant="subtitle2" color="text.secondary" mb={1}>
            Linked Events ({selectedEvents.length})
          </Typography>
          <Box display="flex" flexWrap="wrap" gap={1}>
            {selectedEvents.map((event, index) => (
              <Chip
                key={index}
                label={`${event.symbol} - ${getEventDescription(event)}`}
                onDelete={() => handleRemoveEvent(event)}
                color="primary"
                variant="outlined"
                size="small"
              />
            ))}
          </Box>
        </Box>
      )}

      {/* Events List */}
      <Card>
        <CardContent>
          <Typography variant="subtitle1" fontWeight={600} mb={2}>
            Available Events
          </Typography>

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          {loading ? (
            <Box display="flex" justifyContent="center" p={3}>
              <DWLFLoader size="medium" />
            </Box>
          ) : events.length === 0 ? (
            <Typography color="text.secondary" textAlign="center" py={3}>
              No events found. Try adjusting the filters.
            </Typography>
          ) : (
            <List sx={{ maxHeight: 300, overflow: 'auto' }}>
              {events.map((event, index) => {
                const eventKey = `${event.symbol}_${event.eventType}_${event.date}`;
                const isSelected = selectedEvents.find(e => 
                  `${e.symbol}_${e.eventType}_${e.date}` === eventKey
                );

                return (
                  <ListItem
                    key={index}
                    sx={{
                      border: '1px solid',
                      borderColor: 'divider',
                      borderRadius: 1,
                      mb: 1,
                      backgroundColor: isSelected ? 'action.selected' : 'transparent'
                    }}
                  >
                    <ListItemText
                      primary={
                        <Box display="flex" alignItems="center" gap={1}>
                          {getEventIcon(event)}
                          <Typography variant="body1" fontWeight={600}>
                            {event.symbol}
                          </Typography>
                          <Chip
                            label={getEventTypeLabel(event.eventType)}
                            size="small"
                            variant="outlined"
                            sx={{ textTransform: 'capitalize' }}
                          />
                        </Box>
                      }
                                             secondary={
                         <Box>
                           <Typography variant="body2">
                             {getEventDescription(event)}
                           </Typography>
                           <Box display="flex" gap={2} alignItems="center">
                             <Typography variant="caption" color="text.secondary">
                               {formatDate(event.date)}
                             </Typography>
                             {event.confidenceScore && (
                               <Chip
                                 label={`${Math.round(event.confidenceScore * 100)}%`}
                                 size="small"
                                 variant="outlined"
                                 color="primary"
                               />
                             )}
                           </Box>
                         </Box>
                       }
                    />
                    <ListItemSecondaryAction>
                      <IconButton
                        edge="end"
                        onClick={() => isSelected ? handleRemoveEvent(event) : handleAddEvent(event)}
                        color={isSelected ? 'error' : 'primary'}
                      >
                        {isSelected ? <RemoveIcon /> : <AddIcon />}
                      </IconButton>
                    </ListItemSecondaryAction>
                  </ListItem>
                );
              })}
            </List>
          )}
        </CardContent>
      </Card>
    </Box>
  );
};

export default EventPicker;
